import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, ErrorComponent } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'
import { Grid } from '@mui/material'

import { useUpdateMembershipDynamicFieldsMutation } from '@/api/mem/memberMembershipQueryOptions'
import { membershipDynamicFieldsQueryOptions } from '@/api/membershipQueryOptions'
import {
    membershipDynamicFieldsSchema,
    MembershipDynamicFieldsType,
} from '@/api/memberships'
import { NoPermissionError } from '@/api/NoPermissionError'
import { organizationDynamicFieldsQueryOptions } from '@/api/organizationsQueryOptions'
import DynamicCheckFields from '@/components/form/membership/DynamicCheckFields'
import DynamicDateFields from '@/components/form/membership/DynamicDateFields'
import DynamicTextFields from '@/components/form/membership/DynamicTextFields'
import FormDebug from '@/components/FormDebug'
import { NoPermissionErrorComponent } from '@/components/NoPermissionErrorComponent'
import { getOrganization } from '@/store/auth/authSlice'
import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/dynamicFields'
)({
    component: MembershipDynamicFieldsComponent,
    errorComponent: ({ error }) => {
        if (error instanceof NoPermissionError) {
            return (
                <NoPermissionErrorComponent error={error}></NoPermissionErrorComponent>
            )
        }
        return <ErrorComponent error={error}></ErrorComponent>
    },
})

function MembershipDynamicFieldsComponent() {
    const params = Route.useParams()

    const organization = useSelector(getOrganization)

    const membershipQuery = useSuspenseQuery(
        membershipDynamicFieldsQueryOptions(params.memberUuid),
    )
    const membership = membershipQuery.data
    const [membershipData, setMembershipData] = React.useState<MembershipDynamicFieldsType | undefined>(
        undefined,
    )

    const organizationDynamicFieldsQuery = useSuspenseQuery(
        organizationDynamicFieldsQueryOptions(organization.uuid),
    )
    const organizationDynamicFields = organizationDynamicFieldsQuery.data

    const updateMembershipDynamicFieldsMutation =
        useUpdateMembershipDynamicFieldsMutation(membership.uuid)

    const {
        control,
        register,
        handleSubmit,
        formState: { isDirty, isValid, errors },
    } = useForm<MembershipDynamicFieldsType>({
        resolver: yupResolver<MembershipDynamicFieldsType, any, any>(
            membershipDynamicFieldsSchema,
        ),
        mode: 'onBlur',
        shouldFocusError: true,
    })

    const onSubmit = (data: MembershipDynamicFieldsType) => {

        const isChanged =
            JSON.stringify(membershipData || data) !=
            JSON.stringify({ ...membership, ...data })

        if (isValid && isChanged) {
            updateMembershipDynamicFieldsMutation.mutate({ ...membership, ...data })
            enqueueSnackbar(t('action.changed', { entity: t('entity.dynamicFields') }))
            setMembershipData({ ...membership, ...data })
            console.log({ ...membership, ...data })
        } else {
            console.log(errors)
        }
    }

    const onError = () => {
        console.log('error')
    }

    const writeable = membership.permissions
        ? membership.permissions.some((perm) => perm === 'write')
        : false

    const dynamicFieldsExist = organizationDynamicFields.dynamicField1 ||
        organizationDynamicFields.dynamicField2 ||
        organizationDynamicFields.dynamicField3 ||
        organizationDynamicFields.dynamicField4 ||
        organizationDynamicFields.dynamicField5 ||
        organizationDynamicFields.dateField1 ||
        organizationDynamicFields.dateField2 ||
        organizationDynamicFields.dateField3 ||
        organizationDynamicFields.dateField4 ||
        organizationDynamicFields.dateField5 ||
        organizationDynamicFields.checkField1 ||
        organizationDynamicFields.checkField2 ||
        organizationDynamicFields.checkField3 ||
        organizationDynamicFields.checkField4 ||
        organizationDynamicFields.checkField5

    if (membershipQuery.isLoading || membershipQuery.isFetching) {
        return <LoadProgress />
    }

    return (<>
        {!dynamicFieldsExist &&
            <Grid container spacing={2}>
                <Grid size={{
                    xs: 12
                }}>
                    {t('label.noDynamicFields')}
                </Grid>
            </Grid>
        }
        <form onBlur={handleSubmit(onSubmit, onError)}>
            <Grid container spacing={2}>
                <input
                    type="hidden"
                    {...register('uuid')}
                    defaultValue={membership.uuid}
                />
                <Grid
                    size={{
                        xs: 12,
                        md: 4
                    }}>
                    <DynamicTextFields
                        register={register}
                        membership={membership}
                        organizationDynamicFields={organizationDynamicFields}
                        writeable={writeable}
                    />
                </Grid>

                <Grid
                    size={{
                        xs: 12,
                        md: 4
                    }}>
                    <DynamicCheckFields
                        register={register}
                        membership={membership}
                        organizationDynamicFields={organizationDynamicFields}
                        writeable={writeable}
                    />
                </Grid>

                <Grid
                    size={{
                        xs: 12,
                        md: 4
                    }}>
                    <DynamicDateFields
                        control={control}
                        membership={membership}
                        organizationDynamicFields={organizationDynamicFields}
                        writeable={writeable}
                    />
                </Grid>
            </Grid>
        </form>
        <FormDebug
            errors={errors}
            dirty={isDirty}
            valid={isValid}
            value={membership}
        />
        {import.meta.env.VITE_DEBUG === 'true' &&
            <DevTool control={control} />
        }
    </>);
}
